import React, { useEffect, useRef } from "react";

interface PassengersDropdownProps {
    isOpen: boolean;
    onClose: () => void;
    adults: number;
    children: number;
    infants: number;
    onPassengersChange: (
        adults: number,
        children: number,
        infants: number
    ) => void;
    anchorRef: React.RefObject<HTMLDivElement>;
}

const PassengersDropdown: React.FC<PassengersDropdownProps> = ({
    isOpen,
    onClose,
    adults,
    children,
    infants,
    onPassengersChange,
    anchorRef,
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                anchorRef.current &&
                !anchorRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [isOpen, onClose, anchorRef]);

    if (!isOpen) return null;

    const getTotalPassengers = () => adults + children + infants;

    const getPassengerText = () => {
        const total = getTotalPassengers();
        return total === 1 ? "1 traveler" : `${total} travelers`;
    };

    const handleAdultsChange = (delta: number) => {
        const newAdults = Math.max(1, Math.min(9, adults + delta));
        // Ensure we don't have more infants than adults
        const maxInfants = newAdults;
        const adjustedInfants = Math.min(infants, maxInfants);
        onPassengersChange(newAdults, children, adjustedInfants);
    };

    const handleChildrenChange = (delta: number) => {
        const newChildren = Math.max(0, Math.min(8, children + delta));
        onPassengersChange(adults, newChildren, infants);
    };

    const handleInfantsChange = (delta: number) => {
        // Infants cannot exceed number of adults
        const maxInfants = adults;
        const newInfants = Math.max(0, Math.min(maxInfants, infants + delta));
        onPassengersChange(adults, children, newInfants);
    };

    return (
        <div
            ref={dropdownRef}
            className="absolute top-full bg-white border border-gray-200 rounded-lg shadow-sm z-50"
            style={{
                width: "400px",
                left: "0",
            }}
        >
            <div className="px-4 py-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-sas-text">
                        Travelers
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-sas-blue hover:text-sas-blue-dark text-2xl"
                    >
                        ×
                    </button>
                </div>

                {/* Description */}
                <p className="text-gray-600 mb-8 text-base bg-[#f3f9fc] px-4 py-2 rounded-lg">
                    Select the number of travelers including yourself, fill in
                    the details later.
                </p>

                {/* Passenger counters */}
                <div className="space-y-6">
                    {/* Adults */}
                    <div className="flex items-center justify-between">
                        <div className="text-sas-text text-base">Adults</div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => handleAdultsChange(-1)}
                                disabled={adults <= 1}
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xl ${
                                    adults <= 1
                                        ? "bg-[#879efc] cursor-not-allowed"
                                        : "bg-sas-blue hover:bg-sas-blue-dark"
                                }`}
                            >
                                −
                            </button>
                            <div className="w-12 h-10 border border-gray-300 rounded-lg flex items-center justify-center text-base">
                                {adults}
                            </div>
                            <button
                                onClick={() => handleAdultsChange(1)}
                                disabled={adults >= 9}
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xl ${
                                    adults >= 9
                                        ? "bg-[#879efc] cursor-not-allowed"
                                        : "bg-sas-blue hover:bg-sas-blue-dark"
                                }`}
                            >
                                +
                            </button>
                        </div>
                    </div>

                    {/* Children */}
                    <div className="flex items-center justify-between">
                        <div className="text-sas-text text-base">
                            Children (2-11 years)
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => handleChildrenChange(-1)}
                                disabled={children <= 0}
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xl ${
                                    children <= 0
                                        ? "bg-[#879efc] cursor-not-allowed"
                                        : "bg-sas-blue hover:bg-sas-blue-dark"
                                }`}
                            >
                                −
                            </button>
                            <div className="w-12 h-10 border border-gray-300 rounded-lg flex items-center justify-center text-base">
                                {children}
                            </div>
                            <button
                                onClick={() => handleChildrenChange(1)}
                                disabled={children >= 8}
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xl ${
                                    children >= 8
                                        ? "bg-[#879efc] cursor-not-allowed"
                                        : "bg-sas-blue hover:bg-sas-blue-dark"
                                }`}
                            >
                                +
                            </button>
                        </div>
                    </div>

                    {/* Infants */}
                    <div className="flex items-center justify-between">
                        <div className="text-sas-text text-base">
                            Infants (0-23 months)
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => handleInfantsChange(-1)}
                                disabled={infants <= 0}
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xl ${
                                    infants <= 0
                                        ? "bg-[#879efc] cursor-not-allowed"
                                        : "bg-sas-blue hover:bg-sas-blue-dark"
                                }`}
                            >
                                −
                            </button>
                            <div className="w-12 h-10 border border-gray-300 rounded-lg flex items-center justify-center text-base">
                                {infants}
                            </div>
                            <button
                                onClick={() => handleInfantsChange(1)}
                                disabled={infants >= adults}
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xl ${
                                    infants >= adults
                                        ? "bg-[#879efc] cursor-not-allowed"
                                        : "bg-sas-blue hover:bg-sas-blue-dark"
                                }`}
                            >
                                +
                            </button>
                        </div>
                    </div>
                </div>

                {/* Total and Done button */}
                <div className="flex items-center justify-end gap-4 mt-8">
                    <span className="text-gray-600 text-lg">
                        {getPassengerText()}
                    </span>
                    <button
                        onClick={onClose}
                        className="px-8 py-3 rounded-full font-semibold transition-colors bg-sas-blue text-white hover:bg-sas-blue-dark"
                    >
                        Done
                    </button>
                </div>
            </div>

            {/* Additional options - Full width gray background */}
            <div className="bg-[#f5f5f5] px-4 py-4 space-y-0 rounded-b-lg">
                <button className="flex items-center justify-between w-full text-left p-2 transition-colors group">
                    <div className="flex-1">
                        <div
                            className="font-semibold text-base group-hover:underline"
                            style={{ color: "#00248f" }}
                        >
                            Youth
                        </div>
                        <div className="text-gray-600 text-sm mt-1">
                            Special fares for travelers between 12 and 25 years
                            old
                        </div>
                    </div>
                    <svg
                        className="w-5 h-5 ml-6 flex-shrink-0"
                        style={{ color: "#00248f" }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </button>

                <button className="flex items-center justify-between w-full text-left p-2 transition-colors group">
                    <div className="flex-1">
                        <div
                            className="font-semibold text-base group-hover:underline"
                            style={{ color: "#00248f" }}
                        >
                            Unaccompanied minors
                        </div>
                        <div className="text-gray-600 text-sm mt-1">
                            For children aged 5-15
                        </div>
                    </div>
                    <svg
                        className="w-5 h-5 ml-6 flex-shrink-0"
                        style={{ color: "#00248f" }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </button>

                <button className="flex items-center justify-between w-full text-left p-2 transition-colors group">
                    <div className="flex-1">
                        <div
                            className="font-semibold text-base group-hover:underline"
                            style={{ color: "#00248f" }}
                        >
                            Group
                        </div>
                        <div className="text-gray-600 text-sm mt-1">
                            For groups bigger than 9
                        </div>
                    </div>
                    <svg
                        className="w-5 h-5 ml-6 flex-shrink-0"
                        style={{ color: "#00248f" }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default PassengersDropdown;
